import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { motion } from 'framer-motion';
import { Layers, Droplets, ArrowLeftRight, Coins } from 'lucide-react';
import { resolveCurrencyName } from '../../utils/currencyLookup';

const BridgeConverterNode = ({ id, data }) => {
  const {
    name,
    currencyid,
    fullyqualifiedname,
    currencies = [],
    weights = [],
    initialsupply,
    prelaunchcarveout,
    initialcontributions = [],
    gatewayconverterissuance,
    idregistrationfees,
    idreferrallevels,
    idimportfees,
    options,
    parentName,
  } = data;

  // Resolve currency names
  const resolvedCurrencies = currencies.map((currId) => 
    resolveCurrencyName(currId)
  );

  // Calculate if it's a fractional currency (options & 1)
  const isFractional = options && (options & 1) !== 0;
  const isGatewayConverter = options && (options & 512) !== 0;

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-slate-800 rounded-xl border-2 border-emerald-500/50 shadow-xl min-w-[360px] max-w-[400px]"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-t-lg px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
            <Layers className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-white text-sm">
              {fullyqualifiedname || `${name}.${parentName}`}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-white/70 text-xs">Bridge / Gateway Converter</span>
              {isFractional && (
                <span className="px-1.5 py-0.5 bg-white/20 rounded text-[10px] text-white">
                  Fractional
                </span>
              )}
              {isGatewayConverter && (
                <span className="px-1.5 py-0.5 bg-white/20 rounded text-[10px] text-white">
                  Gateway
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Liquidity Pool Visualization */}
        <div className="bg-slate-900/60 rounded-lg p-3 border border-slate-700/50">
          <div className="flex items-center gap-2 mb-2">
            <Droplets className="w-4 h-4 text-emerald-400" />
            <span className="text-xs font-medium text-slate-300">Liquidity Pool Composition</span>
          </div>
          
          <div className="space-y-2">
            {resolvedCurrencies.map((currency, index) => {
              const weight = weights[index] || 0;
              const percentage = (weight * 100).toFixed(0);
              const initialContrib = initialcontributions[index] || 0;
              
              return (
                <div key={index} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${
                        index === 0 ? 'bg-blue-500' : 
                        index === 1 ? 'bg-purple-500' : 
                        index === 2 ? 'bg-amber-500' : 'bg-pink-500'
                      }`} />
                      <span className="text-white font-medium">{currency}</span>
                    </div>
                    <span className="text-slate-400">{percentage}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${
                        index === 0 ? 'bg-blue-500' : 
                        index === 1 ? 'bg-purple-500' : 
                        index === 2 ? 'bg-amber-500' : 'bg-pink-500'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  {initialContrib > 0 && (
                    <div className="text-[10px] text-slate-500 pl-5">
                      Initial: {initialContrib.toLocaleString()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Conversion Flow Visualization */}
        <div className="flex items-center justify-center gap-2 py-2">
          {resolvedCurrencies.slice(0, 2).map((currency, index) => (
            <React.Fragment key={index}>
              {index > 0 && (
                <ArrowLeftRight className="w-4 h-4 text-emerald-400" />
              )}
              <div className="flex flex-col items-center gap-1">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  index === 0 ? 'bg-blue-500/20 border border-blue-500/50' : 
                  'bg-purple-500/20 border border-purple-500/50'
                }`}>
                  <Coins className={`w-4 h-4 ${
                    index === 0 ? 'text-blue-400' : 'text-purple-400'
                  }`} />
                </div>
                <span className="text-[10px] text-slate-500">{currency}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          {initialsupply !== undefined && (
            <div className="bg-slate-900/60 rounded-lg p-2 border border-slate-700/50">
              <span className="text-slate-400 block">Initial Supply</span>
              <span className="text-white font-medium">{initialsupply?.toLocaleString()}</span>
            </div>
          )}
          
          {gatewayconverterissuance !== undefined && gatewayconverterissuance > 0 && (
            <div className="bg-slate-900/60 rounded-lg p-2 border border-slate-700/50">
              <span className="text-slate-400 block">Gateway Issuance</span>
              <span className="text-white font-medium">{gatewayconverterissuance?.toLocaleString()}</span>
            </div>
          )}

          {prelaunchcarveout !== undefined && prelaunchcarveout > 0 && (
            <div className="bg-slate-900/60 rounded-lg p-2 border border-slate-700/50">
              <span className="text-slate-400 block">Prelaunch Carveout</span>
              <span className="text-white font-medium">{(prelaunchcarveout * 100).toFixed(2)}%</span>
            </div>
          )}

          {idregistrationfees !== undefined && (
            <div className="bg-slate-900/60 rounded-lg p-2 border border-slate-700/50">
              <span className="text-slate-400 block">ID Reg. Fees</span>
              <span className="text-white font-medium">{idregistrationfees}</span>
            </div>
          )}

          {idreferrallevels !== undefined && (
            <div className="bg-slate-900/60 rounded-lg p-2 border border-slate-700/50">
              <span className="text-slate-400 block">Referral Levels</span>
              <span className="text-white font-medium">{idreferrallevels}</span>
            </div>
          )}

          {idimportfees !== undefined && (
            <div className="bg-slate-900/60 rounded-lg p-2 border border-slate-700/50">
              <span className="text-slate-400 block">ID Import Fees</span>
              <span className="text-white font-medium">{idimportfees}</span>
            </div>
          )}
        </div>

        {/* Options Badge */}
        {options && (
          <div className="flex items-center gap-2 pt-2 border-t border-slate-700/50">
            <span className="text-[10px] text-slate-500">Options:</span>
            <span className="px-2 py-0.5 bg-slate-700 rounded text-[10px] text-slate-300 font-mono">
              {options} (0x{options.toString(16).toUpperCase()})
            </span>
          </div>
        )}

        {/* Currency ID */}
        {currencyid && (
          <div className="pt-2 border-t border-slate-700/50">
            <span className="text-[10px] text-slate-500 block">Currency ID</span>
            <span className="text-[10px] text-slate-400 font-mono break-all">{currencyid}</span>
          </div>
        )}
      </div>

      {/* Handles */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !bg-emerald-500 !border-2 !border-slate-800"
      />
    </motion.div>
  );
};

export default BridgeConverterNode;
